/**
 * Range-layered behaviour (`ProductOverview.md` §4.5).
 *
 * The interface changes shape with distance, because GPS truth changes with distance. Above
 * thirty metres a bearing is trustworthy and the arrow is confident. Below it, relative GPS
 * error becomes comparable to the separation itself, so pointing would be false precision —
 * the product says so and switches to hot/cold. At fifteen metres it hands over to human eyes.
 *
 * Every threshold has hysteresis, because someone standing still on a boundary must not watch
 * the interface flicker between two modes.
 */

import { RANGE } from './config';

export type RangeMode =
  /** Confident bearing: the full arrow. */
  | 'arrow'
  /** Too close for a trustworthy bearing: a pulsing proximity ring. */
  | 'hotcold'
  /** Close enough to look around: "you're basically there". */
  | 'handoff';

/**
 * Which mode a distance calls for, given the mode currently showing.
 *
 * Passing `previous` is what makes the switch sticky: you have to travel `hysteresisMetres`
 * past a threshold to cross it, not merely wobble on it.
 */
export function rangeModeFor(
  distanceMetres: number | null,
  previous: RangeMode = 'arrow',
): RangeMode {
  if (distanceMetres === null || !Number.isFinite(distanceMetres)) return 'arrow';

  const slack = RANGE.hysteresisMetres;

  // Coming from further out, a threshold is crossed at its face value; going back out, it takes
  // an extra few metres. So each mode holds on a little rather than flickering.
  const handoffAt = previous === 'handoff' ? RANGE.handoffMetres + slack : RANGE.handoffMetres;
  if (distanceMetres <= handoffAt) return 'handoff';

  const hotColdAt = previous === 'arrow' ? RANGE.hotColdMetres : RANGE.hotColdMetres + slack;
  if (distanceMetres <= hotColdAt) return 'hotcold';

  return 'arrow';
}

/**
 * How urgent the proximity pulse should be, from 0 (at the edge of hot/cold range) to 1
 * (touching). Drives both the pulse rate and the haptics.
 */
export function proximityHeat(distanceMetres: number | null): number {
  if (distanceMetres === null || !Number.isFinite(distanceMetres)) return 0;
  if (distanceMetres <= 0) return 1;
  if (distanceMetres >= RANGE.hotColdMetres) return 0;
  return 1 - distanceMetres / RANGE.hotColdMetres;
}

/** Milliseconds between pulses, from a lazy beat far out to a rapid one up close. */
export function pulseIntervalMs(distanceMetres: number | null): number {
  const slowest = 1400;
  const fastest = 260;
  return Math.round(slowest - (slowest - fastest) * proximityHeat(distanceMetres));
}

/**
 * Tracks how long a pair has been inside the handoff range.
 *
 * Convergence is "within fifteen metres for ten seconds" — a moment, not an instant, so that
 * one optimistic GPS sample cannot declare success while two people are still looking around.
 */
export class ConvergenceWatcher {
  private since: number | null = null;
  private converged = false;

  /** @returns true the first time convergence is reached, and every call after. */
  update(distanceMetres: number | null, now: number): boolean {
    if (distanceMetres === null || distanceMetres > RANGE.handoffMetres) {
      this.since = null;
      return this.converged;
    }

    this.since ??= now;
    if (now - this.since >= RANGE.convergedHoldMs) this.converged = true;

    return this.converged;
  }

  /** How far through the hold we are, 0 to 1. Lets the UI show it filling rather than jumping. */
  progress(now: number): number {
    if (this.converged) return 1;
    if (this.since === null) return 0;
    return Math.min(1, (now - this.since) / RANGE.convergedHoldMs);
  }

  get hasConverged(): boolean {
    return this.converged;
  }

  reset(): void {
    this.since = null;
    this.converged = false;
  }
}
